import { useState, useEffect, useRef } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import SubscriptionPackageModalForm from "./modal";
import ActionPopover from "../../../../../components/popovers/actionPopover";
import DeleteModal from "../../../../../components/modals/DeleteModal";
import useEditFormStore from "../../../../../state-management/useEditFormStore";
import { useGetCountry } from "../../../../../services/country.service";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../../helpers/useManageTable";
import { APP_CONFIG } from "../../../../../helpers/appHelpers";
import TableFilter from "../../../../../components/table/filter/TableFilter";
import { useGetPromotion } from "../../../../../services/promotion.service";
import {
	DetailedSubscriptionTierDto,
	PLAN,
} from "../../../../../models/subscription-tier.dto";
import {
	useDeleteSubscriptionTier,
	useGetSubscriptionTier,
} from "../../../../../services/subscription-tier.service";
import { useGetCurrency } from "../../../../../services/currency.service";
import ManageTableColumns from "../../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../../helpers/useColumnVisibilityModel";
import {
	SubscriptionPackagesColumns,
	PromotionsColumnVisibilityModel,
} from "./tanstack-columns";
import TanStackEmployeeTable from "../../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../../models/table-configuration.dto";
import useUserStore from "../../../../../state-management/useUserStore";

const SubscriptionPackages = () => {
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const { tableKey, columnVisibilityModel, setColumnVisibilityModel } =
		useColumnVisibilityModel(PromotionsColumnVisibilityModel);
	const currentUser = useUserStore().currentUser.user;

	const { tableAPIRef, tableData, isLoading, pageCount, setTableData } =
		useManageTableWithSearchParams<DetailedSubscriptionTierDto>({
			fetchHook: useGetSubscriptionTier,
			options: { detailed: true },
		});

	// Fetched data for select fields requiring data from database
	const options = {
		active: true,
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
		orderBy: "name:asc" as any,
	};
	const { data: countries } = useGetCountry(options);
	const { data: currencies } = useGetCurrency(options);
	const { data: promotions } = useGetPromotion({
		...options,
		orderBy: "title:asc" as any,
	});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenModal(true);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteSubscriptionTier } = useDeleteSubscriptionTier();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		deleteSubscriptionTier(
			{ id: formDataValues?.id },
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	const additionalColumns = [
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			minWidth: 150,
			flex: 1,
			headerClassName: "super-app-theme--header",
			cellClassName: "actions ",
			getActions: (params: any) => {
				return [
					<GridActionsCellItem
						icon={<BiDotsHorizontalRounded />}
						label="actions"
						onClick={(e: any) => {
							// console.log(params.row, "params");
							setFormData(params?.row);
							setAnchorEl(e.currentTarget);
						}}
					/>,
				];
			},
		},
	];

	return (
		<div>
			<h2 className="text-2sm font-semibold mb-3">Subscription Packages</h2>
			<hr />
			<div className="flex justify-between items-center mt-7 mb-8">
				<div className="flex items-center gap-3">
					<TableFilter
						fields={[
							{
								title: "",
								fieldName: "",
								fieldType: "SORT",
								options: [
									{
										title: "Name",
										fieldName: "name",
									},
									{
										title: "Price",
										fieldName: "priceAmount",
									},
									{
										title: "Date Created",
										fieldName: "createdAt",
									},
								],
							},
							{
								title: "Promotion",
								fieldName: "promotionId",
								fieldType: "SELECT_FIELD",
								options: promotions?.data || [],
							},
							{
								title: "Country",
								fieldName: "countryId",
								fieldType: "SELECT_FIELD",
								options: countries?.data || [],
							},
							{
								title: "Currency",
								fieldName: "priceCurrencyId",
								fieldType: "SELECT_FIELD",
								options: currencies?.data || [],
							},
							{
								title: "Active",
								fieldName: "active",
								fieldType: "YES_NO",
							},
							{
								title: "Plan",
								fieldName: "plan",
								fieldType: "CHECKBOX_LIST",
								options: PLAN,
							},
						]}
					/>
					<ManageTableColumns
						columns={SubscriptionPackagesColumns}
						defaultVisibilityModel={PromotionsColumnVisibilityModel}
						visibilityModel={columnVisibilityModel}
						setVisibilityModel={setColumnVisibilityModel}
						tableApiRef={tableAPIRef}
					/>
				</div>
				<div
					className="flex items-center text-primary-blue text-sm cursor-pointer"
					onClick={handleOpenModal}
				>
					<AiOutlinePlus className="mr-1 text-sm" />
					Add New Subscription Package
				</div>
			</div>
			<TanStackEmployeeTable
				ref={tableAPIRef}
				tableId={TableId.SUBSCRIPTION_PACKAGES}
				data={tableData}
				columns={SubscriptionPackagesColumns}
				columnVisibility={columnVisibilityModel}
				onColumnVisibilityChange={setColumnVisibilityModel}
				loading={isLoading}
				onRowClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={{}}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			{/* <CustomStyledTable
				key={tableKey}
				rows={tableData}
				columns={columnsWithAdditions}
				apiRef={tableAPIRef}
				columnVisibilityModel={columnVisibilityModel}
				loading={isLoading}
				pageCount={pageCount}
				setAnchorEl={setAnchorEl}
			/> */}
			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={setOpenModal}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.name || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openModal && (
				<SubscriptionPackageModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={
						isEditForm
							? "Update Subscription Package"
							: "Add New Subscription Package"
					}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					countries={countries?.data || []}
					promotions={promotions?.data || []}
					currencies={currencies?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
		</div>
	);
};

export default SubscriptionPackages;
